/**
 * The kitchen's capacity: what it has actually done, and what it would take.
 *
 * Two halves that answer different questions, deliberately kept apart.
 *
 * **What the log says** is measured. Every real batch marked ready on the To
 * cook page leaves a `cook_rounds` row, and that row records which orders cooked
 * together, when the batch could have started and when it came off. That is
 * enough to split an order's time with the kitchen into the part that was
 * waiting for a burner, the part that was waiting for the wok to fill, and the
 * part that was actually cooking — a split that wants two opposite fixes, which
 * is why it is worth drawing.
 *
 * **Can this kitchen keep up?** is a model, and says so. Little's Law gives the
 * capacity and the load; the wait term is Allen–Cunneen. Nothing here is saved
 * and nothing here is a reading — the inputs start from the shop's own settings
 * and its own arrivals, and a merchant can move them to see what another station
 * or a shorter round would buy. The page's job is to make the trade-off legible,
 * not to make the decision.
 *
 * A page of its own rather than a card in Settings, which is where the two knobs
 * it is about are actually set. Reading a number against a model is a different
 * job from changing a setting, and it wants the room.
 *
 * `cook_rounds` only started filling when it shipped, so an account can arrive
 * here with nothing measured. That case says so plainly instead of drawing a
 * confident split from zero rows — the same stance the estimate-learning card
 * takes below ten samples.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react-native';
import {
  capacityModel,
  formatDuration,
  roundWindowModel,
  squaredVariation,
  type CapacityVerdict,
  type MessageKey,
} from '@yummy/shared';
import { fetchCookInsights } from '@yummy/db';
import {
  Callout,
  CardGrid,
  ErrorBanner,
  HeadingStat,
  HourStrip,
  IconControl,
  LoadingState,
  PageHeading,
  ProgressBar,
  Row,
  Screen,
  Stack,
  StatTile,
  Stepper,
  Text,
  TitledCard,
  useAsync,
  useResponsive,
  useT,
  type Tone,
} from '@yummy/ui-rnr';

import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Stacked cards size to their own content.
 *
 * `TitledCard` carries `h-full` so that a *row* of them is one height — right in a
 * grid cell, wrong in a column. `height: 100%` against a column whose own height is
 * derived from its content makes every card as tall as the whole stack, so four
 * cards became four screenfuls of white with the content pinned to the top of each.
 * These four are stacked, so they opt out; the pages that put these cards in rows
 * (the dashboard, the cook page's tickets) still want the default.
 */
const STACKED_CARD = 'h-auto';

/** The word for each verdict, and the colour it is said in. */
const VERDICT: Record<CapacityVerdict, { key: MessageKey; tone: Tone }> = {
  calm: { key: 'merchant.capacity.verdictCalm', tone: 'success' },
  busy: { key: 'merchant.capacity.verdictBusy', tone: 'info' },
  critical: { key: 'merchant.capacity.verdictCritical', tone: 'warning' },
  over: { key: 'merchant.capacity.verdictOver', tone: 'danger' },
};

/** The range each stepper allows, matching what the database will accept. */
const AT_ONCE_RANGE = { min: 1, max: 50 };
const COOK_MINUTES_RANGE = { min: 1, max: 240 };
const ARRIVALS_RANGE = { min: 0, max: 200 };
const WINDOW_MINUTES_RANGE = { min: 1, max: 60 };

/**
 * Where three label-and-stepper rows stop fitting side by side.
 *
 * The kit's own three-column rung (`useResponsive().columns`): 1080 is where three
 * comfortable cards fit. It is the right anchor here because a stepper cell is wide —
 * a label has to sit beside a control that is ~100pt on its own — so the moment
 * three stop fitting, the labels wrap to three lines and the row reads as broken.
 * Below it, each takes a row to itself, which is what a squeezed three-across looks
 * like it is trying and failing to do anyway.
 *
 * Two-item rows keep the softer `isTablet` rung: a tile holding one value has far
 * less to fit than a label beside a stepper.
 */
const THREE_ACROSS_MIN_WIDTH = 1080;

/**
 * Renders the capacity page.
 *
 * @returns The measured split and the what-if calculator.
 */
export function CapacityScreen() {
  const t = useT();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;
  const { width, isTablet } = useResponsive();
  const threeAcross = width >= THREE_ACROSS_MIN_WIDTH;

  const insights = useAsync(
    async () => (restaurantId ? fetchCookInsights(supabase, restaurantId) : null),
    [restaurantId],
  );
  const data = insights.data;

  // ---------------------------------------------------------------------------
  // The what-if, seeded once from what the shop already has
  // ---------------------------------------------------------------------------
  // Plain state, never saved: the inputs are a question, not a setting, and the
  // two numbers that *are* settings live in Settings where changing them was
  // already explained.
  const [ordersAtOnce, setOrdersAtOnce] = useState(3);
  const [cookMinutes, setCookMinutes] = useState(15);
  const [arrivals, setArrivals] = useState(0);
  const [windowMinutes, setWindowMinutes] = useState(10);

  // Which restaurant the inputs were seeded from. A ref rather than state
  // because re-seeding must not itself cause a render — and keyed by restaurant
  // so switching shops does not silently carry the last one's numbers over.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !restaurantId || insights.loading) return;
    if (seededFor.current === restaurantId) return;
    seededFor.current = restaurantId;

    setOrdersAtOnce(data.kitchen_capacity);
    // The measured cook time where there is one, otherwise the shop's own
    // baseline — the same fallback the estimator makes.
    setCookMinutes(
      Math.max(1, Math.round((data.mean_cook_seconds || data.default_prep_seconds) / 60)),
    );
    setArrivals(data.peak_orders_per_hour || data.mean_orders_per_hour);
    setWindowMinutes(Math.max(1, Math.round(data.cook_round_seconds / 60)));
  }, [data, restaurantId, insights.loading]);

  // How uneven the cooking is, once the log can say. With nothing measured the
  // model assumes an exponential service time (Cs² = 1), which is the
  // conservative middle rather than a claim about this kitchen.
  const serviceVariability = squaredVariation(
    data?.mean_cook_seconds ?? 0,
    data?.mean_cook_square_seconds2 ?? 0,
  );

  const model = useMemo(
    () =>
      capacityModel({
        ordersAtOnce,
        cookSeconds: cookMinutes * 60,
        arrivalsPerHour: arrivals,
        serviceVariability,
        arrivalVariability: 1,
      }),
    [ordersAtOnce, cookMinutes, arrivals, serviceVariability],
  );

  const windowModel = useMemo(
    () => roundWindowModel(windowMinutes * 60, arrivals),
    [windowMinutes, arrivals],
  );

  const verdict = VERDICT[model.verdict];

  if (insights.initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.orders.loading')} />
      </Screen>
    );
  }

  return (
    <Screen constrain>
      <Stack gap="lg">
        <PageHeading
          title={t('merchant.capacity.title')}
          minWidth={260}
          subtitle={
            <Row gap="sm" align="center" wrap>
              <HeadingStat>
                {t('merchant.capacity.ordersAtOnce')}: {ordersAtOnce}
              </HeadingStat>
              {data ? (
                <HeadingStat>
                  {t('merchant.capacity.arrivalsPeak', {
                    hour: data.peak_hour,
                    count: data.peak_orders_per_hour,
                  })}
                </HeadingStat>
              ) : null}
            </Row>
          }
          action={
            <IconControl
              label={insights.loading ? t('common.refreshing') : t('common.refresh')}
              icon={RefreshCw}
              onPress={insights.reload}
              disabled={insights.loading}
            />
          }
        />

        {insights.error ? (
          <ErrorBanner message={insights.error} onRetry={insights.reload} />
        ) : null}

        {data ? (
          <>
            {/* ------------------------------------------------------------------
                Measured: what the logbook actually saw
            ------------------------------------------------------------------ */}
            <TitledCard
              className={STACKED_CARD}
              title={t('merchant.capacity.logTitle')}
              description={t('merchant.capacity.logHint', { days: data.window_days })}>
              {data.round_count === 0 ? (
                <Callout tone="info" size="base" message={t('merchant.capacity.logEmpty')} />
              ) : (
                <Stack gap="md">
                  {data.is_learning ? (
                    <Callout
                      tone="warning"
                      message={t('merchant.capacity.logLearning', { count: data.round_count })}
                    />
                  ) : null}
                  {/* How much evidence there is, as a line rather than two cards. Two
                      tiles for two counts left a third of a row empty and made the
                      provenance the loudest thing on the card; the split below is what
                      the page exists to draw. */}
                  <Text className="text-subtle-foreground text-sm">
                    {t('merchant.capacity.logSummary', {
                      batches: data.round_count,
                      orders: data.order_count,
                      size: data.median_batch_size,
                    })}
                  </Text>
                  {/* The split, three across so the row is full and the labels are not
                      squeezed into wrapping. */}
                  <CardGrid columns={threeAcross ? 3 : 1} minWidth={200}>
                    <StatTile
                      label={t('merchant.capacity.capacityWait')}
                      value={formatDuration(data.median_capacity_wait_seconds, t)}
                      hint={t('merchant.capacity.capacityWaitHint')}
                    />
                    <StatTile
                      label={t('merchant.capacity.batchWait')}
                      value={formatDuration(data.median_batch_wait_seconds, t)}
                      hint={t('merchant.capacity.batchWaitHint')}
                    />
                    <StatTile
                      label={t('merchant.capacity.cook')}
                      value={formatDuration(data.median_cook_seconds, t)}
                      hint={t('merchant.capacity.cookHint', {
                        mean: formatDuration(data.mean_cook_seconds, t),
                      })}
                    />
                  </CardGrid>
                </Stack>
              )}
            </TitledCard>

            {/* ------------------------------------------------------------------
                Measured: when the work arrives
            ------------------------------------------------------------------ */}
            <TitledCard
              className={STACKED_CARD}
              title={t('merchant.capacity.arrivalsTitle')}
              description={t('merchant.capacity.arrivalsHint')}>
              <Stack gap="sm">
                <HourStrip
                  hours={data.arrivals_by_hour.map((entry) => ({
                    hour: entry.hour,
                    value: entry.per_hour,
                  }))}
                />
                <Text className="text-subtle-foreground text-sm">
                  {t('merchant.capacity.arrivalsPeak', {
                    hour: data.peak_hour,
                    count: data.peak_orders_per_hour,
                  })}
                </Text>
              </Stack>
            </TitledCard>

            {/* ------------------------------------------------------------------
                The model: what it would take
            ------------------------------------------------------------------ */}
            <TitledCard
              className={STACKED_CARD}
              title={t('merchant.capacity.calcTitle')}
              description={t('merchant.capacity.calcHint')}>
              <Stack gap="lg">
                <CardGrid columns={threeAcross ? 3 : 1} minWidth={220}>
                  <NumberStepper
                    label={t('merchant.capacity.ordersAtOnce')}
                    hint={t('merchant.capacity.ordersAtOnceHint')}
                    value={ordersAtOnce}
                    onChange={setOrdersAtOnce}
                    {...AT_ONCE_RANGE}
                  />
                  <NumberStepper
                    label={t('merchant.capacity.cookMinutes')}
                    hint={t('merchant.capacity.cookMinutesHint')}
                    value={cookMinutes}
                    onChange={setCookMinutes}
                    {...COOK_MINUTES_RANGE}
                  />
                  <NumberStepper
                    label={t('merchant.capacity.arrivals')}
                    hint={t('merchant.capacity.arrivalsRateHint', {
                      count: data.peak_orders_per_hour,
                    })}
                    value={arrivals}
                    onChange={setArrivals}
                    {...ARRIVALS_RANGE}
                  />
                </CardGrid>

                {/* The lead number, drawn as a bar so "nearly full" reads before
                    the percentage is read. */}
                <Stack gap="xs">
                  <Row spread align="baseline">
                    <Text className="text-sm font-semibold">{t('merchant.capacity.load')}</Text>
                    <Text className="text-sm font-bold">{Math.round(model.load * 100)}%</Text>
                  </Row>
                  <ProgressBar
                    progress={model.load}
                    tone={
                      model.verdict === 'over'
                        ? 'danger'
                        : model.verdict === 'critical'
                          ? 'warning'
                          : 'brand'
                    }
                  />
                  <Text className="text-subtle-foreground text-xs">
                    {t('merchant.capacity.loadHint')}
                  </Text>
                </Stack>

                <CardGrid columns={isTablet ? 2 : 1} minWidth={200}>
                  <StatTile
                    label={t('merchant.capacity.clearable')}
                    value={String(Math.round(model.clearablePerHour))}
                    hint={t('merchant.capacity.clearableHint')}
                  />
                  <StatTile
                    label={t('merchant.capacity.wait')}
                    // A tilde because this is the model's answer, not the log's:
                    // the measured wait is the tile at the top of the page.
                    value={
                      model.waitSeconds === null
                        ? t('merchant.capacity.cannotKeepUp')
                        : `≈ ${formatDuration(model.waitSeconds, t)}`
                    }
                    hint={
                      model.waitSeconds === null
                        ? t('merchant.capacity.cannotKeepUpHint')
                        : t('merchant.capacity.waitHint')
                    }
                  />
                </CardGrid>

                <Callout tone={verdict.tone} size="base" message={t(verdict.key)} />

                {/* The model's own boundary, on the card that has it. The numbers
                    above are about the kitchen as a whole, and a merchant who sets
                    "orders at a time" to their three woks has no way to know that
                    from the figure alone. See `docs/cook-rounds.md`. */}
                <Text className="text-subtle-foreground text-xs">
                  {t('merchant.capacity.singlePool')}
                </Text>
              </Stack>
            </TitledCard>

            {/* ------------------------------------------------------------------
                The batch, which is the other half of the wait
            ------------------------------------------------------------------ */}
            <TitledCard
              className={STACKED_CARD}
              title={t('merchant.capacity.windowTitle')}
              description={t('merchant.capacity.windowHint')}>
              <Stack gap="lg">
                <NumberStepper
                  label={t('merchant.capacity.windowCovers')}
                  value={windowMinutes}
                  onChange={setWindowMinutes}
                  {...WINDOW_MINUTES_RANGE}
                />

                <CardGrid columns={isTablet ? 2 : 1} minWidth={200}>
                  <StatTile
                    label={t('merchant.capacity.windowBatch')}
                    value={windowModel.batchSize.toFixed(1)}
                    hint={t('merchant.capacity.windowBatchHint')}
                  />
                  <StatTile
                    label={t('merchant.capacity.windowAdds')}
                    value={formatDuration(windowModel.batchWaitSeconds, t)}
                    hint={t('merchant.capacity.windowAddsHint')}
                  />
                </CardGrid>

                <Stack gap="xs">
                  <Text className="text-subtle-foreground text-sm">
                    {t('merchant.capacity.windowTradeoff')}
                  </Text>
                  <Text className="text-subtle-foreground text-xs">
                    {t('merchant.capacity.windowSetting')}
                  </Text>
                </Stack>
              </Stack>
            </TitledCard>
          </>
        ) : null}
      </Stack>
    </Screen>
  );
}

/**
 * A labelled stepper, which is what every input on this page is.
 *
 * Local rather than in the kit: the three fields differ only in their words, and
 * a page-specific arrangement of three shared primitives is cheaper to read here
 * than a general component with a shape nobody else wants.
 *
 * @param props The label, the optional explanation, the value and its bounds.
 * @returns The row.
 */
function NumberStepper({
  label,
  hint,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
}) {
  return (
    <Stack gap="xs">
      {/* The unit rides in the label rather than floating at the end of the row.
          "Minutes per order (mins)" says what the number is wherever it is read —
          in the heading, in the accessible name, in a screenshot — where a "min"
          parked after the buttons is only legible next to them. */}
      <Row spread align="center" gap="sm">
        <Text className="text-sm font-semibold">{label}</Text>
        <Stepper
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          floor={Math.max(min, 1)}
          what={label}
        />
      </Row>
      {hint ? <Text className="text-subtle-foreground text-xs">{hint}</Text> : null}
    </Stack>
  );
}
